import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget (Wave 52.2) — web implementation. Loads
 * the Turnstile script once, renders the widget explicitly, and
 * reports the token (or null on expiry/error) to the parent. The
 * native variant (TurnstileGate.tsx) renders nothing: Turnstile is
 * browser-only, and the native support form stays token-free — the
 * server only enforces the captcha for web-declared submissions.
 *
 * NOTE: the widget only renders on hostnames allowed in the Cloudflare
 * Turnstile dashboard for this site key (homestead.ninja,
 * app.homestead.ninja, homestead.expo.app, localhost for dev).
 */

const SITE_KEY = '0x4AAAAAAEr0F1ndb2EuJ7dA';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
  reset: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptLoading: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptLoading) {
    scriptLoading = new Promise((resolve, reject) => {
      // eslint-disable-next-line no-undef
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptLoading = null;
        reject(new Error('turnstile script failed to load'));
      };
      // eslint-disable-next-line no-undef
      document.head.appendChild(s);
    });
  }
  return scriptLoading;
}

export function TurnstileGate({ onToken }: { onToken: (token: string | null) => void }) {
  // eslint-disable-next-line no-undef
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    loadScript()
      .then(() => {
        if (!alive || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          theme: 'light',
          size: 'flexible',
          callback: (t: string) => onToken(t),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      })
      .catch(() => onToken(null));
    return () => {
      alive = false;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          // widget already gone (page teardown) — nothing to clean up
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} style={{ marginTop: 14 }} />;
}

/** Reset the widget after a failed submit — tokens are single-use. */
export function resetTurnstile(): void {
  try {
    window.turnstile?.reset();
  } catch {
    // no widget mounted — nothing to reset
  }
}
